import React, { useRef, useEffect } from "react";
import "./NeonTrail.css";

/* ── Tuning constants ─────────────────────────────────── */
const MAX_POINTS = 120; // trail length (more points for fast movement)
const FADE_RATE = 0.93; // exponential decay per frame
const CORE_WIDTH = 3.5; // bright center stroke (thicker)
const PARTICLE_COUNT = 18; // ambient floating particles
const MAX_GAP = 8; // max pixel gap before we interpolate extra points
const GLOW_LAYERS = [
  { width: 38, alpha: 0.05 }, // outermost soft glow
  { width: 26, alpha: 0.1 },
  { width: 16, alpha: 0.2 },
  { width: 8, alpha: 0.4 },
]; // layered glow — no CSS filter needed

/* ── Color helpers ────────────────────────────────────── */
const neonRGB = "0, 200, 255"; // primary neon blue
const coreRGB = "180, 240, 255"; // bright core highlight
const particleRGB = "0, 180, 255"; // ambient particles

/* ── Ambient particle class ───────────────────────────── */
class Particle {
  constructor(w, h) {
    this.reset(w, h, true);
  }
  reset(w, h, initial = false) {
    this.x = Math.random() * w;
    this.y = Math.random() * h;
    this.r = Math.random() * 1.6 + 0.4;
    this.baseAlpha = Math.random() * 0.25 + 0.05;
    this.alpha = this.baseAlpha;
    this.vx = (Math.random() - 0.5) * 0.15;
    this.vy = (Math.random() - 0.5) * 0.15;
    this.phase = Math.random() * Math.PI * 2;
    this.speed = Math.random() * 0.008 + 0.003;
    if (initial) this.life = Math.random();
    else this.life = 1;
  }
  update(w, h, time) {
    this.x += this.vx;
    this.y += this.vy;
    this.alpha =
      this.baseAlpha *
      (0.5 + 0.5 * Math.sin(time * this.speed * 60 + this.phase));
    this.life -= 0.0008;
    if (
      this.x < -10 ||
      this.x > w + 10 ||
      this.y < -10 ||
      this.y > h + 10 ||
      this.life <= 0
    ) {
      this.reset(w, h);
    }
  }
}

const NeonTrail = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: false });

    let w, h, dpr;
    const points = []; // { x, y, alpha }
    const pendingPositions = []; // buffered mouse positions between frames
    let hasMoved = false;
    let isTouch = false;
    let particles = [];
    let raf;

    /* ── Resize ──────────────────────────────────────── */
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2); // cap at 2× for perf
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // re-init particles on resize
      particles = Array.from(
        { length: PARTICLE_COUNT },
        () => new Particle(w, h)
      );
    };
    resize();

    /* ── Input handlers — buffer ALL positions between frames ── */
    const onMouseMove = (e) => {
      if (isTouch) return;
      pendingPositions.push({ x: e.clientX, y: e.clientY });
      hasMoved = true;
    };
    const onTouchMove = (e) => {
      isTouch = true;
      const t = e.touches[0];
      if (!t) return;
      pendingPositions.push({ x: t.clientX, y: t.clientY });
      hasMoved = true;
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("resize", resize, { passive: true });

    /* ── Draw smooth quadratic Bézier through points ── */
    const drawSmoothTrail = (pts, lineWidth, colorRGB, alphaMultiplier) => {
      if (pts.length < 2) return;
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = lineWidth;
      ctx.globalCompositeOperation = "lighter"; // additive blending for glow

      // Build a single smooth path using quadratic curves
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);

      for (let i = 1; i < pts.length - 1; i++) {
        const xc = (pts[i].x + pts[i + 1].x) * 0.5;
        const yc = (pts[i].y + pts[i + 1].y) * 0.5;
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, xc, yc);
      }
      // Last segment
      const last = pts[pts.length - 1];
      ctx.lineTo(last.x, last.y);

      // Use gradient stroke along the path for fading tail
      const first = pts[0];
      const grad = ctx.createLinearGradient(first.x, first.y, last.x, last.y);
      const tailAlpha = Math.max(first.alpha * alphaMultiplier, 0);
      const headAlpha = Math.max(last.alpha * alphaMultiplier, 0);
      grad.addColorStop(0, `rgba(${colorRGB}, ${tailAlpha * 0.05})`);
      grad.addColorStop(0.3, `rgba(${colorRGB}, ${tailAlpha * 0.4})`);
      grad.addColorStop(1, `rgba(${colorRGB}, ${headAlpha})`);
      ctx.strokeStyle = grad;
      ctx.stroke();
      ctx.restore();
    };

    /* ── Cursor tip glow ─────────────────────────────── */
    const drawTipGlow = (x, y, alpha) => {
      const radius = 28;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
      grad.addColorStop(0, `rgba(${coreRGB}, ${alpha * 0.7})`);
      grad.addColorStop(0.2, `rgba(${neonRGB}, ${alpha * 0.3})`);
      grad.addColorStop(0.55, `rgba(${neonRGB}, ${alpha * 0.08})`);
      grad.addColorStop(1, `rgba(${neonRGB}, 0)`);
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.restore();
    };

    /* ── Ambient particles ───────────────────────────── */
    const drawParticles = (time) => {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      for (const p of particles) {
        p.update(w, h, time);
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3);
        grad.addColorStop(0, `rgba(${particleRGB}, ${p.alpha})`);
        grad.addColorStop(1, `rgba(${particleRGB}, 0)`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }
      ctx.restore();
    };

    /* ── Main animation loop ─────────────────────────── */
    let time = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      time += 0.016;
      ctx.clearRect(0, 0, w, h);

      // Draw ambient particles (always visible)
      drawParticles(time);

      if (!hasMoved) return;

      // Drain all buffered mouse positions & interpolate gaps
      const lastPt = points.length > 0 ? points[points.length - 1] : null;
      let prev = lastPt ? { x: lastPt.x, y: lastPt.y } : null;

      for (const pos of pendingPositions) {
        if (prev) {
          const dx = pos.x - prev.x;
          const dy = pos.y - prev.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          // If the cursor jumped far, insert intermediate points
          if (dist > MAX_GAP) {
            const steps = Math.ceil(dist / MAX_GAP);
            for (let s = 1; s < steps; s++) {
              const t = s / steps;
              points.push({
                x: prev.x + dx * t,
                y: prev.y + dy * t,
                alpha: 1,
              });
            }
          }
        }
        points.push({ x: pos.x, y: pos.y, alpha: 1 });
        prev = pos;
      }
      pendingPositions.length = 0; // clear buffer

      // If no new positions this frame, keep last known position alive
      if (points.length === 0) return;
      while (points.length > MAX_POINTS) points.shift();

      // Fade existing points with exponential decay
      for (let i = 0; i < points.length; i++) {
        const age = (points.length - 1 - i) / points.length;
        points[i].alpha *= FADE_RATE;
        // Extra fade for older points
        if (age > 0.7) points[i].alpha *= 0.97;
      }
      // Prune dead points
      while (points.length > 0 && points[0].alpha < 0.005) points.shift();

      if (points.length < 2) return;

      // Draw glow layers (wide → narrow, low → high alpha) — no blur filter
      for (const layer of GLOW_LAYERS) {
        drawSmoothTrail(points, layer.width, neonRGB, layer.alpha);
      }
      // Core bright line
      drawSmoothTrail(points, CORE_WIDTH, coreRGB, 0.85);

      // Cursor tip glow
      const tip = points[points.length - 1];
      drawTipGlow(tip.x, tip.y, tip.alpha);
    };

    raf = requestAnimationFrame(loop);

    /* ── Cleanup ─────────────────────────────────────── */
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="neon-trail-canvas" />;
};

export default NeonTrail;
